import axios, { AxiosInstance, AxiosResponse } from 'axios'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

// Debug logging to diagnose environment variable issues
console.log('🔍 API Configuration Debug:')
console.log('  VITE_API_URL from env:', import.meta.env.VITE_API_URL)
console.log('  API_BASE_URL being used:', API_BASE_URL)
console.log('  All VITE_ env vars:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')))

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: any) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: any) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - remove token and redirect
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// API Response Types
export interface LoginResponse {
  access_token: string
  token_type: string
  user: {
    user_id: number
    username: string
    email: string
    full_name: string
    role: string
    is_active: boolean
    created_at: string
    updated_at: string
  }
}

export interface User {
  user_id: number
  username: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Employee {
  id?: number
  employee_id: string  // Backend uses string for employee_id
  first_name: string
  last_name: string
  full_name?: string   // Computed field
  email?: string
  phone?: string
  department?: string
  position?: string
  hire_date: string
  hourly_rate?: number
  salary?: number
  is_active: boolean
  status?: string      // Computed from is_active
  created_at?: string
  updated_at?: string
}

export interface Order {
  order_id: number
  order_number: string
  customer_name: string
  product_type: string
  quantity: number
  unit_price: number
  total_amount: number
  order_date: string
  due_date: string
  status: string
  priority: string
  description?: string
  created_at: string
  updated_at: string
}

export interface DashboardMetrics {
  total_orders: number
  active_orders: number
  completed_orders: number
  total_employees: number
  active_employees: number
  total_revenue: number
  monthly_revenue: number
  production_efficiency: number
  order_completion_rate: number
  employee_utilization: number
}

// Authentication API
export const authAPI = {
  login: async (credentials: { username: string; password: string }): Promise<LoginResponse> => {
    const formData = new FormData()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)
    
    const response = await apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    return response.data
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/profile')
    return response.data
  },

  changePassword: async (passwords: { current_password: string; new_password: string }): Promise<void> => {
    await apiClient.post('/auth/change-password', passwords)
  },
}

// Dashboard API
export const dashboardAPI = {
  getMetrics: async (): Promise<DashboardMetrics> => {
    const response = await apiClient.get('/dashboard/metrics')
    return response.data
  },

  getRecentOrders: async (limit: number = 10): Promise<Order[]> => {
    const response = await apiClient.get(`/dashboard/recent-orders?limit=${limit}`)
    return response.data
  },

  getRecentEmployees: async (limit: number = 10): Promise<Employee[]> => {
    const response = await apiClient.get(`/dashboard/recent-employees?limit=${limit}`)
    return response.data
  },
}

// Orders API
export const ordersAPI = {
  getOrders: async (params?: {
    skip?: number
    limit?: number
    status?: string
    priority?: string
    search?: string
  }): Promise<{ orders: Order[]; total: number }> => {
    // Convert priority string to integer for backend and rename parameter
    const backendParams: any = { ...params }
    if (backendParams.priority) {
      const priorityMap: { [key: string]: number } = {
        'low': 1,
        'medium': 2,
        'high': 3,
        'urgent': 4
      }
      // Backend expects priority_level, not priority
      backendParams.priority_level = priorityMap[backendParams.priority] || 2
      delete backendParams.priority // Remove the frontend parameter
    }
    
    // Map search parameter to q for backend
    if (backendParams.search) {
      backendParams.q = backendParams.search
      delete backendParams.search
    }
    
    const response = await apiClient.get('/orders', { params: backendParams })
    // Transform backend response to match frontend expectations
    const backendData = response.data
    return {
      orders: backendData.items?.map((item: any) => ({
        ...item,
        order_id: item.id, // Map id to order_id
        product_type: item.product_description, // Map product_description to product_type
        priority: item.priority_level === 1 ? 'low' :
                 item.priority_level === 2 ? 'medium' :
                 item.priority_level === 3 ? 'high' :
                 item.priority_level === 4 ? 'urgent' :
                 item.priority_level === 5 ? 'urgent' : 'medium' // Map priority_level to priority string
      })) || [],
      total: backendData.total || 0
    }
  },

  getOrder: async (orderId: number): Promise<Order> => {
    const response = await apiClient.get(`/orders/${orderId}`)
    const item = response.data
    return {
      ...item,
      order_id: item.id,
      product_type: item.product_description,
      priority: item.priority_level === 1 ? 'low' :
               item.priority_level === 2 ? 'medium' :
               item.priority_level === 3 ? 'high' :
               item.priority_level === 4 ? 'urgent' :
               item.priority_level === 5 ? 'urgent' : 'medium'
    }
  },

  createOrder: async (orderData: Partial<Order>): Promise<Order> => {
    const response = await apiClient.post('/orders', orderData)
    const item = response.data
    return {
      ...item,
      order_id: item.id,
      product_type: item.product_description,
      priority: item.priority_level === 1 ? 'low' :
               item.priority_level === 2 ? 'medium' :
               item.priority_level === 3 ? 'high' :
               item.priority_level === 4 ? 'urgent' :
               item.priority_level === 5 ? 'urgent' : 'medium'
    }
  },

  updateOrder: async (orderId: number, orderData: Partial<Order>): Promise<Order> => {
    const response = await apiClient.put(`/orders/${orderId}`, orderData)
    const item = response.data
    return {
      ...item,
      order_id: item.id,
      product_type: item.product_description,
      priority: item.priority_level === 1 ? 'low' :
               item.priority_level === 2 ? 'medium' :
               item.priority_level === 3 ? 'high' :
               item.priority_level === 4 ? 'urgent' :
               item.priority_level === 5 ? 'urgent' : 'medium'
    }
  },

  deleteOrder: async (orderId: number): Promise<void> => {
    await apiClient.delete(`/orders/${orderId}`)
  },

  updateOrderStatus: async (orderId: number, status: string): Promise<Order> => {
    const response = await apiClient.patch(`/orders/${orderId}/status`, { status })
    const item = response.data
    return {
      ...item,
      order_id: item.id,
      product_type: item.product_description,
      priority: item.priority_level === 1 ? 'low' :
               item.priority_level === 2 ? 'medium' :
               item.priority_level === 3 ? 'high' :
               item.priority_level === 4 ? 'urgent' :
               item.priority_level === 5 ? 'urgent' : 'medium'
    }
  },

  exportOrder: async (orderId: number, format: 'pdf' | 'csv' = 'pdf'): Promise<Blob> => {
    const response = await apiClient.get(`/orders/${orderId}/export`, {
      params: { format },
      responseType: 'blob'
    })
    return response.data
  },

  exportOrders: async (params?: {
    status?: string
    priority?: string
    start_date?: string
    end_date?: string
    format?: 'pdf' | 'csv'
  }): Promise<Blob> => {
    const response = await apiClient.get('/orders/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  },
}

// Employees API
export const employeesAPI = {
  getEmployees: async (params?: {
    skip?: number
    limit?: number
    department?: string
    status?: string
    search?: string
  }): Promise<{ employees: Employee[]; total: number }> => {
    // Map frontend parameters to backend parameters
    const backendParams: any = { ...params }
    if (backendParams.search) {
      backendParams.q = backendParams.search
      delete backendParams.search
    }
    if (backendParams.status) {
      // Map status to is_active for backend
      if (backendParams.status === 'active') {
        backendParams.is_active = true
      } else if (backendParams.status === 'inactive') {
        backendParams.is_active = false
      }
      delete backendParams.status
    }
    
    const response = await apiClient.get('/employees', { params: backendParams })
    // Transform backend response to match frontend expectations
    const backendData = response.data
    return {
      employees: backendData.items?.map((item: any) => ({
        ...item,
        status: item.is_active ? 'active' : 'inactive', // Add computed status field
      })) || [],
      total: backendData.total || 0
    }
  },

  getEmployee: async (employeeId: number): Promise<Employee> => {
    const response = await apiClient.get(`/employees/${employeeId}`)
    const item = response.data
    return {
      ...item,
      status: item.is_active ? 'active' : 'inactive'
    }
  },

  createEmployee: async (employeeData: Partial<Employee>): Promise<Employee> => {
    // Transform frontend data to backend format
    const backendData: any = { ...employeeData }
    
    // Handle status to is_active mapping
    if (backendData.status) {
      backendData.is_active = backendData.status === 'active'
      delete backendData.status
    }
    
    const response = await apiClient.post('/employees', backendData)
    const item = response.data
    return {
      ...item,
      status: item.is_active ? 'active' : 'inactive'
    }
  },

  updateEmployee: async (employeeId: number, employeeData: Partial<Employee>): Promise<Employee> => {
    // Transform frontend data to backend format
    const backendData: any = { ...employeeData }
    
    // Handle status to is_active mapping
    if (backendData.status) {
      backendData.is_active = backendData.status === 'active'
      delete backendData.status
    }
    
    const response = await apiClient.put(`/employees/${employeeId}`, backendData)
    const item = response.data
    return {
      ...item,
      status: item.is_active ? 'active' : 'inactive'
    }
  },

  deleteEmployee: async (employeeId: number): Promise<void> => {
    await apiClient.delete(`/employees/${employeeId}`)
  },

  getEmployeeAssignments: async (employeeId: number): Promise<any[]> => {
    const response = await apiClient.get(`/employees/${employeeId}/assignments`)
    return response.data
  },
}

// Attendance API
export interface AttendanceRecord {
  id: number
  employee_id: number
  attendance_date: string
  status: AttendanceStatus
  check_in_time?: string
  check_out_time?: string
  hours_worked?: number
  notes?: string
  created_at: string
  updated_at: string
  employee_name?: string
  employee_id_number?: string
  department?: string
  position?: string
}

export enum AttendanceStatus {
  PRESENT = 'present',
  ABSENT = 'absent',
  LATE = 'late',
  HALF_DAY = 'half_day',
  SICK_LEAVE = 'sick_leave',
  VACATION = 'vacation',
  PERSONAL_LEAVE = 'personal_leave'
}

export interface AttendanceSummary {
  employee_id: number
  employee_name: string
  employee_id_number: string
  department?: string
  position?: string
  total_days: number
  present_days: number
  absent_days: number
  late_days: number
  half_days: number
  sick_leave_days: number
  vacation_days: number
  personal_leave_days: number
  attendance_percentage: number
  total_hours_worked: number
}

export interface AttendanceCalendar {
  employee_id: number
  employee_name: string
  month: number
  year: number
  attendance_data: Record<string, string>
}

export interface AttendanceStats {
  total_employees: number
  present_today: number
  absent_today: number
  late_today: number
  attendance_percentage_today: number
  monthly_average: number
  trend_data: Array<{
    date: string
    attendance_percentage: number
  }>
}

export interface MonthlyAttendanceReport {
  month: number
  year: number
  total_employees: number
  total_working_days: number
  average_attendance_percentage: number
  employee_summaries: AttendanceSummary[]
}

export const attendanceAPI = {
  getRecords: async (params?: {
    employee_id?: number
    start_date?: string
    end_date?: string
    status?: AttendanceStatus
    department?: string
    skip?: number
    limit?: number
  }): Promise<AttendanceRecord[]> => {
    const response = await apiClient.get('/attendance', { params })
    return response.data
  },

  createRecord: async (data: Partial<AttendanceRecord>): Promise<AttendanceRecord> => {
    const response = await apiClient.post('/attendance', data)
    return response.data
  },

  updateRecord: async (id: number, data: Partial<AttendanceRecord>): Promise<AttendanceRecord> => {
    const response = await apiClient.put(`/attendance/${id}`, data)
    return response.data
  },

  deleteRecord: async (id: number): Promise<void> => {
    await apiClient.delete(`/attendance/${id}`)
  },

  getSummary: async (employeeId: number, startDate: string, endDate: string): Promise<AttendanceSummary> => {
    const response = await apiClient.get(`/attendance/summary/${employeeId}`, {
      params: { start_date: startDate, end_date: endDate }
    })
    return response.data
  },

  getCalendar: async (employeeId: number, month: number, year: number): Promise<AttendanceCalendar> => {
    const response = await apiClient.get(`/attendance/calendar/${employeeId}`, {
      params: { month, year }
    })
    return response.data
  },

  getStats: async (): Promise<AttendanceStats> => {
    const response = await apiClient.get('/attendance/stats')
    return response.data
  },

  getMonthlyReport: async (month: number, year: number): Promise<MonthlyAttendanceReport> => {
    const response = await apiClient.get('/attendance/reports/monthly', {
      params: { month, year }
    })
    return response.data
  },

  checkInOut: async (data: {
    employee_id: number
    action: 'check_in' | 'check_out'
    notes?: string
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/attendance/check-in-out', data)
    return response.data
  },

  markAbsent: async (employeeId: number, date: string, reason?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/attendance/mark-absent', null, {
      params: { employee_id: employeeId, attendance_date: date, reason }
    })
    return response.data
  },

  markPresent: async (employeeId: number, date: string, checkInTime?: string, checkOutTime?: string): Promise<{ success: boolean; message: string }> => {
    const params: any = { employee_id: employeeId, attendance_date: date }
    if (checkInTime) params.check_in_time = checkInTime
    if (checkOutTime) params.check_out_time = checkOutTime
    
    const response = await apiClient.post('/attendance/mark-present', null, { params })
    return response.data
  },

  bulkCreate: async (data: {
    employee_ids: number[]
    attendance_date: string
    status: AttendanceStatus
    notes?: string
  }): Promise<{ success: boolean; message: string; processed_count: number; failed_count: number; errors: string[] }> => {
    const response = await apiClient.post('/attendance/bulk', data)
    return response.data
  },

  getTodayAttendance: async (): Promise<{
    date: string
    total_records: number
    status_breakdown: Record<string, number>
    records: AttendanceRecord[]
  }> => {
    const response = await apiClient.get('/attendance/today')
    return response.data
  },

  getEmployeeRecentAttendance: async (employeeId: number, days: number = 7): Promise<AttendanceRecord[]> => {
    const response = await apiClient.get(`/attendance/employee/${employeeId}/recent`, {
      params: { days }
    })
    return response.data
  },

  // New simplified attendance management methods
  ensureDefaults: async (date: string): Promise<{
    success: boolean
    message: string
    created_count: number
    existing_count: number
  }> => {
    const response = await apiClient.post('/attendance/ensure-defaults', null, {
      params: { attendance_date: date }
    })
    return response.data
  },

  bulkUpdate: async (updates: Array<{
    employee_id: number
    attendance_date: string
    status?: string
    hours_worked?: number
    notes?: string
  }>): Promise<{
    success: boolean
    message: string
    updated_count: number
    failed_count: number
    errors: string[]
  }> => {
    // Backend expects direct array, not wrapped in object
    const response = await apiClient.post('/attendance/bulk-update', updates)
    return response.data
  },

  getDailyOverview: async (date: string): Promise<{
    date: string
    total_employees: number
    present_count: number
    absent_count: number
    half_day_count: number
    total_daily_cost: number
    employees: Array<{
      id: number
      employee_id: number
      employee_id_number: string
      employee_name: string
      first_name?: string
      last_name?: string
      full_name?: string
      department?: string
      position?: string
      is_active?: boolean
      attendance_id?: number
      attendance_date: string
      status: string
      hours_worked: number
      check_in_time?: string
      check_out_time?: string
      notes?: string
      created_at?: string
      updated_at?: string
      compensation_structure_type?: string
      effective_hourly_rate?: number
      daily_earnings_estimate?: number
      monthly_salary?: number
    }>
  }> => {
    const response = await apiClient.get('/attendance/daily-overview', {
      params: { attendance_date: date }
    })
    return response.data
  },

  getEmployeeCompensationContext: async (employeeId: number, date?: string): Promise<{
    employee_id: number
    employee_id_number: string
    first_name: string
    last_name: string
    full_name: string
    department?: string
    position?: string
    is_active: boolean
    current_compensation?: {
      structure_id: number
      structure_type: string
      base_rate: number
      currency: string
      overtime_eligible: boolean
      overtime_rate_multiplier: number
      standard_hours_per_day: number
      effective_from: string
      effective_to?: string
    }
    compensation_history: Array<{
      structure_id: number
      structure_type: string
      base_rate: number
      currency: string
      effective_from: string
      effective_to?: string
      is_active: boolean
    }>
  }> => {
    const params: any = {}
    if (date) params.date = date
    const response = await apiClient.get(`/attendance/employee/${employeeId}/compensation-context`, { params })
    return response.data
  },

  getAllEmployeesCalendar: async (month: number, year: number): Promise<{
    month: number
    year: number
    total_employees: number
    employees: Array<{
      employee_id: number
      employee_name: string
      employee_id_number: string
      department?: string
      position?: string
      attendance_data: Record<string, string>
    }>
  }> => {
    const response = await apiClient.get('/attendance/calendar/all-employees', {
      params: { month, year }
    })
    return response.data
  },
}

// Payroll API Types
export interface PayrollRecord {
  id: number
  employee_id: number
  pay_period_year: number
  pay_period_month: number
  gross_pay: number
  deductions: number
  net_pay: number
  pay_date?: string
  status: 'draft' | 'calculated' | 'approved' | 'paid' | 'cancelled'
  calculated_by?: number
  approved_by?: number
  approved_at?: string
  notes?: string
  pay_period_display?: string
  total_earnings?: number
  total_deductions?: number
  total_benefits?: number
  total_taxes?: number
  employee?: any
  payroll_items?: PayrollItem[]
  created_at: string
  updated_at: string
}

export interface PayrollItem {
  id: number
  payroll_record_id: number
  payroll_type_id: number
  amount: number
  hours?: number
  rate?: number
  description?: string
  reference_data?: string
  calculated_amount?: number
  payroll_type?: any
}

export interface PayrollCalculationRequest {
  employee_id: number
  pay_period_year: number
  pay_period_month: number
  include_overtime?: boolean
  overtime_threshold?: number
  custom_rates?: Record<string, any>
  additional_earnings?: any[]
  additional_deductions?: any[]
}

export interface PayrollCalculationResponse {
  employee_id: number
  pay_period_year: number
  pay_period_month: number
  calculation_summary: Record<string, any>
  payroll_items: PayrollItem[]
  gross_pay: number
  total_deductions: number
  net_pay: number
  calculation_details: Record<string, any>
  warnings: string[]
}

export interface PayrollSummaryReport {
  period: string
  total_employees: number
  total_gross_pay: number
  total_net_pay: number
  total_deductions: number
  average_gross_pay: number
  average_net_pay: number
  compensation_breakdown: Record<string, any>
  bonus_summary: Record<string, any>
  deduction_summary: Record<string, any>
  department_summary: Record<string, any>
}

export interface AutomatedPayrollPreview {
  preview_results: Array<{
    employee_id: number
    employee_name?: string
    success: boolean
    gross_pay?: number
    net_pay?: number
    overtime_pay?: number
    holiday_pay?: number
    bonus_pay?: number
    warnings?: string[]
    calculation_summary?: Record<string, any>
    error?: string
  }>
  summary: {
    total_employees: number
    successful_previews: number
    failed_previews: number
    total_gross_pay: number
    total_net_pay: number
    total_overtime_pay: number
    total_holiday_pay: number
    total_bonus_pay: number
    average_gross_pay: number
    average_net_pay: number
  }
}

export interface TaskStatus {
  task_id: string
  status: string
  current?: number
  total?: number
  message?: string
  result?: Record<string, any>
  error?: string
}

// Payroll API
export const payrollAPI = {
  getPayrollRecords: async (params?: {
    skip?: number
    limit?: number
    employee_id?: number
    year?: number
    month?: number
    status?: string
  }): Promise<PayrollRecord[]> => {
    const response = await apiClient.get('/payroll', { params })
    return response.data
  },

  getPayrollById: async (payrollId: number): Promise<PayrollRecord> => {
    const response = await apiClient.get(`/payroll/${payrollId}`)
    return response.data
  },

  calculatePayroll: async (calculationRequest: PayrollCalculationRequest): Promise<PayrollCalculationResponse> => {
    const response = await apiClient.post('/payroll/calculate', calculationRequest)
    return response.data
  },

  updatePayroll: async (payrollId: number, updateData: Partial<PayrollRecord>): Promise<PayrollRecord> => {
    const response = await apiClient.put(`/payroll/${payrollId}`, updateData)
    return response.data
  },

  deletePayroll: async (payrollId: number): Promise<void> => {
    await apiClient.delete(`/payroll/${payrollId}`)
  },

  approvePayroll: async (payrollId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/payroll/${payrollId}/approve`)
    return response.data
  },

  markPayrollPaid: async (payrollId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/payroll/${payrollId}/pay`)
    return response.data
  },

  batchCalculatePayroll: async (batchRequest: {
    employee_ids: number[]
    pay_period_year: number
    pay_period_month: number
    calculation_options?: Record<string, any>
    auto_approve?: boolean
  }): Promise<{
    total_employees: number
    successful_calculations: number
    failed_calculations: number
    calculation_results: any[]
    errors: any[]
    summary: Record<string, any>
  }> => {
    const response = await apiClient.post('/payroll/calculate/batch', batchRequest)
    return response.data
  },

  processPayroll: async (processingRequest: {
    employee_ids: number[]
    pay_period_year: number
    pay_period_month: number
    auto_approve?: boolean
    send_notifications?: boolean
    processing_options?: Record<string, any>
  }): Promise<{
    total_employees: number
    successful_calculations: number
    failed_calculations: number
    auto_approved: number
    processing_results: any[]
    errors: any[]
    warnings: string[]
    summary: Record<string, any>
    processing_time: number
  }> => {
    const response = await apiClient.post('/payroll/process', processingRequest)
    return response.data
  },

  getSummary: async (year: number, month: number): Promise<PayrollSummaryReport> => {
    const response = await apiClient.get('/payroll/reports/summary', {
      params: { year, month }
    })
    return response.data
  },

  getCompensationAnalytics: async (): Promise<{
    total_employees: number
    compensation_structures: Record<string, any>
    average_rates_by_type: Record<string, any>
    salary_ranges: Record<string, any>
    overtime_statistics: Record<string, any>
    bonus_statistics: Record<string, any>
    cost_analysis: Record<string, any>
    trends: Record<string, any>
  }> => {
    const response = await apiClient.get('/payroll/analytics/compensation')
    return response.data
  },

  // Compensation Structure endpoints
  createCompensationStructure: async (structureData: {
    employee_id: number
    structure_type: string
    base_rate: number
    currency?: string
    effective_from: string
    effective_to?: string
    is_active?: boolean
    overtime_eligible?: boolean
    overtime_rate_multiplier?: number
    standard_hours_per_day?: number
    standard_days_per_week?: number
    standard_weeks_per_month?: number
    notes?: string
  }): Promise<any> => {
    const response = await apiClient.post('/payroll/compensation-structures', structureData)
    return response.data
  },

  getCompensationStructure: async (structureId: number): Promise<any> => {
    const response = await apiClient.get(`/payroll/compensation-structures/${structureId}`)
    return response.data
  },

  updateCompensationStructure: async (structureId: number, structureData: any): Promise<any> => {
    const response = await apiClient.put(`/payroll/compensation-structures/${structureId}`, structureData)
    return response.data
  },

  // Employee Bonus endpoints
  createEmployeeBonus: async (bonusData: {
    employee_id: number
    bonus_type: string
    amount: number
    currency?: string
    pay_period_year?: number
    pay_period_month?: number
    effective_date: string
    is_recurring?: boolean
    recurrence_frequency?: string
    is_taxable?: boolean
    description?: string
    notes?: string
    compensation_structure_id?: number
  }): Promise<any> => {
    const response = await apiClient.post('/payroll/bonuses', bonusData)
    return response.data
  },

  getEmployeeBonus: async (bonusId: number): Promise<any> => {
    const response = await apiClient.get(`/payroll/bonuses/${bonusId}`)
    return response.data
  },

  updateEmployeeBonus: async (bonusId: number, bonusData: any): Promise<any> => {
    const response = await apiClient.put(`/payroll/bonuses/${bonusId}`, bonusData)
    return response.data
  },

  deleteEmployeeBonus: async (bonusId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/payroll/bonuses/${bonusId}`)
    return response.data
  },

  manageBonuses: async (managementRequest: {
    action: 'add' | 'update' | 'remove' | 'process'
    employee_ids: number[]
    bonus_data: Record<string, any>
    effective_date?: string
    process_immediately?: boolean
  }): Promise<{
    success: boolean
    message: string
    processed_bonuses: number
    failed_bonuses: number
    bonus_ids: number[]
    errors: string[]
  }> => {
    const response = await apiClient.post('/payroll/bonuses/manage', managementRequest)
    return response.data
  }
}

// Manual Payroll API
export interface ManualPayrollCalculateRequest {
  employee_ids: number[]
  period_start: string
  period_end: string
}

export interface ManualPayrollCalculateResponse {
  success: boolean
  records_created: number
  payroll_records: Array<{
    id: number
    employee_id: number
    employee_name: string
    period_start: string
    period_end: string
    gross_pay: number
    total_deductions: number
    net_pay: number
    is_draft: boolean
    status: string
    items?: PayrollItem[]
  }>
}

export interface DraftPayrollRecord {
  id: number
  employee_id: number
  employee_name: string
  employee_id_number?: string
  department?: string
  position?: string
  period_start: string
  period_end: string
  gross_pay: number
  total_deductions: number
  total_bonuses: number
  net_pay: number
  is_draft: boolean
  status: string
  items: PayrollItem[]
  created_at: string
  updated_at: string
}

export interface DraftPayrollListResponse {
  total: number
  records: DraftPayrollRecord[]
}

export interface FinalizePayrollRequest {
  payroll_ids: number[]
}

export interface FinalizePayrollResponse {
  success: boolean
  finalized_count: number
  records: Array<{
    id: number
    is_draft: boolean
    status: string
    finalized_at: string
    finalized_by: number
  }>
}

export const manualPayrollAPI = {
  calculatePayroll: async (request: ManualPayrollCalculateRequest): Promise<ManualPayrollCalculateResponse> => {
    const response = await apiClient.post('/payroll/manual/calculate', request)
    return response.data
  },

  getDraftPayroll: async (params?: {
    employee_id?: number
    period_start?: string
    period_end?: string
    skip?: number
    limit?: number
  }): Promise<DraftPayrollListResponse> => {
    const response = await apiClient.get('/payroll/manual/draft', { params })
    return response.data
  },

  updateDraftPayroll: async (payrollId: number, updateData: {
    gross_pay?: number
    items?: Array<{
      type: string
      amount: number
      description?: string
    }>
  }): Promise<DraftPayrollRecord> => {
    const response = await apiClient.put(`/payroll/manual/${payrollId}`, updateData)
    return response.data
  },

  deleteDraftPayroll: async (payrollId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/payroll/manual/${payrollId}`)
    return response.data
  },

  finalizePayroll: async (request: FinalizePayrollRequest): Promise<FinalizePayrollResponse> => {
    const response = await apiClient.post('/payroll/manual/finalize', request)
    return response.data
  },

  exportPayroll: async (params?: {
    period_start?: string
    period_end?: string
    employee_ids?: number[]
    format?: 'excel' | 'pdf' | 'json'
  }): Promise<Blob> => {
    const response = await apiClient.get('/payroll/manual/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  }
}

// Holiday API Types
export interface Holiday {
  id: number
  date: string
  name: string
  holiday_type: string
  description?: string
  country_code: string
  is_working_day: boolean
  created_at: string
  updated_at: string
}

export interface HolidayStatistics {
  total_holidays: number
  upcoming_holidays: number
  holidays_by_type: Record<string, number>
  next_holiday?: {
    date: string
    name: string
    days_until: number
  }
}

// Holiday API
export const holidayAPI = {
  getHolidays: async (params?: {
    year?: number
    month?: number
    start_date?: string
    end_date?: string
    holiday_type?: string
    skip?: number
    limit?: number
  }): Promise<{ holidays: Holiday[]; total: number; statistics: HolidayStatistics }> => {
    const response = await apiClient.get('/holidays', { params })
    return response.data
  },

  getUpcomingHolidays: async (days: number = 30): Promise<Holiday[]> => {
    const response = await apiClient.get('/holidays/upcoming', {
      params: { days }
    })
    return response.data
  },

  getHolidaysForMonth: async (year: number, month: number): Promise<Holiday[]> => {
    const response = await apiClient.get(`/holidays/month/${year}/${month}`)
    return response.data
  },

  getHolidayForDate: async (date: string): Promise<Holiday | null> => {
    try {
      const response = await apiClient.get(`/holidays/date/${date}`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  syncHolidays: async (year?: number): Promise<{
    success: boolean
    message: string
    synced_count: number
    new_count: number
    updated_count: number
  }> => {
    const response = await apiClient.post('/holidays/sync', null, {
      params: year ? { year } : undefined
    })
    return response.data
  }
}

export default apiClient
